import React, { useContext, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import DutyCard from './DutyCard'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { Alert, Chip } from '@mui/material'
import AmbulanceIcon from '../../../assets/icons/Ambulance'
import { SiteModeContext } from '../../../components/mainFrame/MainFrame'
import { PTSControlContext } from './PTSControl'
import moment from 'moment'

const getJourneyTime = (journey) =>
  journey?.pickup?.when?.exactTime ||
  journey?.pickup?.when?.patientReadyFrom ||
  journey?.dropoff?.when?.exactTime ||
  journey?.dropoff?.when?.targetArrivalTime

const timeString = (journey) => {
  const time = getJourneyTime(journey)
  return time ? moment(time).format('DD/MM HH:mm') : 'Any Time'
}

const getWardLabel = (loc) =>
  [loc?.wardDepartmentAsAddress?.name || loc?.wardName, loc?.wardLocation]
    .filter(Boolean)
    .join(' - ')

const LeftPanel = ({
  duties = [],
  unassignedJourneys = [],
  onAssign,
  fetchAll,
  setViewJourney,
}) => {
  const navigate = useNavigate()
  const { setSiteMode } = useContext(SiteModeContext)
  const { selectedDuty } = useContext(PTSControlContext)

  const dutyRefs = useRef({})

  useEffect(() => {
    if (!selectedDuty?._id) return
    const el = dutyRefs.current[selectedDuty._id]
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [selectedDuty])

  return (
    <Box
      sx={{
        height: '100%',
        maxHeight: 'calc(100vh - 120px)', // adjust if you have a header
        overflowY: 'auto',
        scrollBehavior: 'smooth',
        pr: 0.5,
      }}
    >
      <Stack gap={2} justifyContent="space-between" height="100%">
        <Stack gap={2}>
          {/* Unassigned Journeys */}
          {unassignedJourneys?.length > 0 && (
            <Stack
              gap={1.25}
              sx={{
                p: 1.25,
                borderRadius: 3,
                background: 'linear-gradient(180deg, #0F172A 0%, #0B1220 100%)',
                border: '1px solid rgba(255,255,255,0.06)',
                boxShadow: `
          0 10px 30px rgba(0, 0, 0, 0.4),
          0 2px 6px rgba(0, 0, 0, 0.3),
          inset 0 1px 0 rgba(255,255,255,0.04)
        `,
              }}
            >
              <Stack gap={1.25}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography
                    variant="h6"
                    fontSize="1rem"
                    fontWeight={700}
                    sx={{ color: '#F8FAFC' }}
                  >
                    Unassigned Journeys
                  </Typography>

                  <Chip
                    label={unassignedJourneys.length}
                    size="small"
                    sx={{
                      ml: 'auto',
                      fontWeight: 700,
                      color: '#F8FAFC',
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  />
                </Stack>

                {unassignedJourneys.map((journey) => (
                  <Box
                    key={journey._id}
                    sx={{
                      p: 1.2,
                      mb: 1,
                      borderRadius: 2,
                      bgcolor: '#fff',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    }}
                  >
                    <Stack
                      gap={1}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      flexWrap="wrap"
                    >
                      <Typography fontWeight={700}>
                        {journey?.booking?.patient?.demographics?.firstName}{' '}
                        {journey?.booking?.patient?.demographics?.lastName}
                      </Typography>
                      <Box
                        sx={{
                          fontSize: 14,
                          color: 'contrast.main',
                          px: 1,
                        }}
                      >
                        {journey?.booking?.patient?.patientID ?? ''}
                      </Box>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {journey.pickup?.name || journey.pickup?.town || 'Pickup'}{' '}
                      -{' '}
                      {journey.dropoff?.name ||
                        journey.dropoff?.town ||
                        'Dropoff'}
                    </Typography>
                    <br />
                    {(getWardLabel(journey?.pickup) ||
                      getWardLabel(journey?.dropoff)) && (
                      <Typography variant="caption" color="text.secondary">
                        {[
                          getWardLabel(journey?.pickup),
                          getWardLabel(journey?.dropoff),
                        ]
                          .filter(Boolean)
                          .join(' + ')}
                      </Typography>
                    )}
                    <Typography variant="subtitle2">
                      {timeString(journey)}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="anBlue"
                      size="small"
                      fullWidth
                      sx={{
                        mt: 1,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 1.5,
                      }}
                      onClick={() => onAssign?.(journey)}
                    >
                      Assign to Duty
                    </Button>
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}
          {/* Active Duties */}
          <Stack
            gap={1.25}
            sx={{
              p: 1.25,
              borderRadius: 3,
              background: 'linear-gradient(180deg, #0F172A 0%, #0B1220 100%)',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow: `
          0 10px 30px rgba(0, 0, 0, 0.4),
          0 2px 6px rgba(0, 0, 0, 0.3),
          inset 0 1px 0 rgba(255,255,255,0.04)
        `,
            }}
          >
            <Stack gap={1.25}>
              <Stack direction="row" alignItems="center" gap={1}>
                {/* <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.07)',
              color: 'contrast.main',
            }}
          >
            <AmbulanceIcon style={{ color: 'white' }} />
          </Box> */}

                <Typography
                  variant="h6"
                  fontSize="1rem"
                  fontWeight={700}
                  sx={{ color: '#F8FAFC' }}
                >
                  Active Duties
                </Typography>

                <Chip
                  label={duties.length}
                  size="small"
                  sx={{
                    ml: 'auto',
                    fontWeight: 700,
                    color: '#F8FAFC',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                />
              </Stack>

              {duties.length ? (
                duties.map((d) => (
                  <Box
                    key={d._id}
                    ref={(el) => {
                      dutyRefs.current[d._id] = el
                    }}
                  >
                    <DutyCard duty={d} onViewJourney={setViewJourney} />
                  </Box>
                ))
              ) : (
                <Stack gap={1}>
                  <Alert
                    severity="info"
                    sx={{
                      backgroundColor: 'rgba(59,130,246,0.12)',
                      color: '#E5F0FF',
                      border: '1px solid rgba(59,130,246,0.18)',
                    }}
                  >
                    No Active Duties
                  </Alert>

                  <Button
                    variant="contained"
                    color="anBlue"
                    onClick={() => {
                      navigate('/pts/planning')
                      setSiteMode('normal')
                    }}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      fontWeight: 700,
                      boxShadow: 'none',
                    }}
                  >
                    View Planning Screen
                  </Button>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
        <Button
          variant="contained"
          color="contrast"
          onClick={() => {
            setSiteMode('normal')
            navigate('/pts/planning')
          }}
        >
          View Planning Screen
        </Button>
      </Stack>
    </Box>
  )
}

export default LeftPanel
